/**
 * Hero line-art motifs — the two decorative schematics behind the branded hero copy
 * (CAOS-131). Both are built as inline SVG so they are CSP-safe self-hosted vector art with
 * ZERO external requests: no `<img>`-to-a-CDN, no eval, no inline `style=` attribute. Every
 * stroke colour reads a design token from a CSS class in `hero.css` (never a hard-coded
 * literal and never a `var()` inside a presentation attribute, which SVG does not resolve), so
 * a brand swap ([data-brand=acme]) recolours the whole schematic with the accent. Geometry,
 * opacity, and width ride SVG presentation attributes, which are NOT inline style.
 *
 *   radar — the sign-in mockup: concentric rings with labelled METADATA / SCHEMA / COMPONENTS
 *           / EVENTS nodes around the authentic mark at the centre.
 *   stack — the empty-workspace mockup: an isometric plate stack ringed by COMPONENTS / SHELL
 *           PACKAGE / APPLICATIONS / METADATA / EVENTS / APIS nodes, mark on the stack.
 *
 * The authentic CAOS mark (never the placeholder triangle) is embedded once, centred, via an
 * SVG `<image>` pointing at the self-hosted, bundler-fingerprinted asset.
 */

const SVG = 'http://www.w3.org/2000/svg';
const XLINK = 'http://www.w3.org/1999/xlink';

/** Which line-art schematic a hero renders. */
export type HeroMotif = 'radar' | 'stack';

/** The value-prop / node glyphs drawn as monoline SVG (no external icon font). */
export type HeroGlyph =
  | 'layers'
  | 'cube'
  | 'nodes'
  | 'shield'
  | 'grid'
  | 'doc'
  | 'bell'
  | 'code'
  | 'components';

type Attrs = Record<string, string | number>;

/** Build an SVG element in the SVG namespace with attributes and children. No inline style. */
function s(tag: string, attrs: Attrs = {}, ...kids: (SVGElement | string)[]): SVGElement {
  const node = document.createElementNS(SVG, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  for (const kid of kids) node.append(typeof kid === 'string' ? document.createTextNode(kid) : kid);
  return node;
}

/**
 * A 24×24 monoline glyph as an SVG `<g>`, translated to centre on (cx, cy). Consumers place
 * these in the value-prop grid (`heroGlyph`) and as node markers inside the motifs. Paths are
 * stroked (never filled) so they read as thin line-art in the accent/muted seam.
 */
export function heroGlyph(kind: HeroGlyph, cx = 12, cy = 12): SVGElement {
  const g = s('g', { transform: `translate(${cx - 12} ${cy - 12})`, class: 'hero-glyph' });
  for (const d of GLYPH_PATHS[kind]) g.append(s('path', { d }));
  return g;
}

/** Monoline path data for each glyph, authored on a 24×24 grid. */
const GLYPH_PATHS: Record<HeroGlyph, readonly string[]> = {
  layers: ['M12 3 3 8l9 5 9-5-9-5Z', 'M3 12l9 5 9-5', 'M3 16l9 5 9-5'],
  cube: ['M12 3 4 7.5v9L12 21l8-4.5v-9L12 3Z', 'M4 7.5 12 12l8-4.5', 'M12 12v9'],
  nodes: ['M7 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z', 'M18 6.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z', 'M18 21.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z', 'M9 6.7l7-2.4M9.3 9.2 16.6 18'],
  shield: ['M12 3 5 6v5c0 4.2 2.9 7.6 7 9 4.1-1.4 7-4.8 7-9V6l-7-3Z'],
  grid: ['M5 5h4v4H5zM15 5h4v4h-4zM5 15h4v4H5zM15 15h4v4h-4z'],
  doc: ['M7 3h7l4 4v14H7V3Z', 'M14 3v4h4', 'M9.5 12h5M9.5 15.5h5'],
  bell: ['M12 4a5 5 0 0 0-5 5c0 5-2 6-2 6h14s-2-1-2-6a5 5 0 0 0-5-5Z', 'M10.2 20a2 2 0 0 0 3.6 0'],
  code: ['M9 8l-4 4 4 4M15 8l4 4-4 4'],
  components: ['M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z'],
};

/** One ring for the radar motif. */
function ring(cx: number, cy: number, r: number, opacity: number): SVGElement {
  return s('circle', {
    cx,
    cy,
    r,
    class: 'hero-line',
    'stroke-opacity': opacity,
    'stroke-width': 1.25,
    fill: 'none',
  });
}

/** A small labelled node: a rounded marker square with a glyph, and a mono caption. */
function node(x: number, y: number, kind: HeroGlyph, label: string, anchor = 'middle'): SVGElement {
  const g = s('g');
  g.append(
    s('rect', { x: x - 22, y: y - 22, width: 44, height: 44, rx: 11, class: 'hero-node-box' }),
    heroGlyph(kind, x, y),
    s('text', { x, y: y + 40, 'text-anchor': anchor, class: 'hero-node-label' }, label),
  );
  return g;
}

/** A faint dashed connector between two points. */
function link(x1: number, y1: number, x2: number, y2: number): SVGElement {
  return s('line', {
    x1,
    y1,
    x2,
    y2,
    class: 'hero-link',
    'stroke-opacity': 0.35,
    'stroke-width': 1,
    'stroke-dasharray': '2 6',
  });
}

/** A tiny accent tick square, scattered to echo the mockup's schematic dust. */
function tick(x: number, y: number): SVGElement {
  return s('rect', { x, y, width: 5, height: 5, class: 'hero-line', 'stroke-opacity': 0.5, fill: 'none' });
}

/** The centred authentic mark, embedded as a self-hosted SVG `<image>` (never the triangle). */
function centreMark(cx: number, cy: number, w: number, markUrl: string): SVGElement {
  const h = w; // the mark asset is square (1254x1254) — preserve its 1:1 aspect ratio
  const img = s('image', {
    x: cx - w / 2,
    y: cy - h / 2,
    width: w,
    height: h,
    href: markUrl,
    preserveAspectRatio: 'xMidYMid meet',
    class: 'hero-center-mark',
  });
  img.setAttributeNS(XLINK, 'xlink:href', markUrl); // legacy attr for maximum renderer support
  return img;
}

/** The sign-in radar: concentric rings + four labelled nodes around the centred mark. */
function radar(markUrl: string): SVGElement {
  const cx = 300;
  const cy = 300;
  const svg = s('svg', {
    viewBox: '0 0 600 600',
    class: 'hero-motif hero-motif--radar',
    'aria-hidden': 'true',
    focusable: 'false',
  });

  svg.append(ring(cx, cy, 250, 0.14), ring(cx, cy, 185, 0.2), ring(cx, cy, 120, 0.28), ring(cx, cy, 62, 0.4));
  // Faint crosshair sweep lines.
  svg.append(
    s('line', { x1: cx, y1: 40, x2: cx, y2: 560, class: 'hero-line', 'stroke-opacity': 0.08, 'stroke-width': 1 }),
    s('line', { x1: 40, y1: cy, x2: 560, y2: cy, class: 'hero-line', 'stroke-opacity': 0.08, 'stroke-width': 1 }),
  );
  // Connectors from centre out to each node.
  svg.append(link(cx, cy, 300, 96), link(cx, cy, 486, 214), link(cx, cy, 150, 452), link(cx, cy, 452, 470));
  // Scattered ticks.
  svg.append(tick(150, 150), tick(470, 150), tick(120, 380), tick(500, 360), tick(360, 500));
  // Labelled nodes: METADATA (top), SCHEMA (right), COMPONENTS (lower-left), EVENTS (lower-right).
  svg.append(
    node(300, 96, 'doc', 'METADATA'),
    node(486, 214, 'grid', 'SCHEMA'),
    node(150, 452, 'components', 'COMPONENTS'),
    node(452, 470, 'bell', 'EVENTS'),
  );
  svg.append(centreMark(cx, cy, 118, markUrl));
  return svg;
}

/** An isometric plate for the empty-workspace stack. */
function plate(cx: number, cy: number, w: number, h: number, opacity: number): SVGElement {
  const d = `M${cx} ${cy - h} ${cx + w} ${cy} ${cx} ${cy + h} ${cx - w} ${cy}Z`;
  return s('path', { d, class: 'hero-line hero-plate', 'stroke-opacity': opacity, 'stroke-width': 1.25 });
}

/** The empty-workspace isometric stack ringed by six labelled capability nodes. */
function stack(markUrl: string): SVGElement {
  const cx = 330;
  const cy = 300;
  // CAOS-212 — the viewBox carries 20 units of horizontal padding either side of the 640-wide
  // node field (same centre x=320, so the schematic does not shift). The rightmost node's caption
  // ("APPLICATIONS", the widest label) is centred at x=588 and its trailing letter previously ran
  // a few units past a tight `0 0 640` right edge and clipped; the padded box gives every caption
  // room to render in full at any width. No node moves and no label resizes.
  const svg = s('svg', {
    viewBox: '-20 0 680 560',
    class: 'hero-motif hero-motif--stack',
    'aria-hidden': 'true',
    focusable: 'false',
  });

  // Stacked iso plates, base to top.
  svg.append(
    plate(cx, cy + 46, 132, 66, 0.16),
    plate(cx, cy + 24, 132, 66, 0.22),
    plate(cx, cy, 132, 66, 0.32),
    plate(cx, cy - 26, 132, 66, 0.5),
  );
  // CAOS-214 — the six capability nodes ring the central mark evenly (matching the reference
  // mockup): WORKSPACE crowns the top slightly left, APPLICATIONS / METADATA / EVENTS descend the
  // right arc, and COMPONENTS / APIS balance the left arc, leaving the stack's front face open at
  // the bottom. Radii are near-equal (~215–275 units) so no side is bunched. Every caption stays
  // inside the padded viewBox: METADATA's right edge lands ~23 units clear of the box edge and
  // COMPONENTS' left edge ~60 units clear. Glyphs and labels are unchanged from CAOS-212.
  // Dashed connectors from the stack out to each surrounding node.
  svg.append(
    link(cx, cy - 26, 284, 66),   // WORKSPACE   (top, slightly left)
    link(cx, cy - 26, 500, 117),  // APPLICATIONS (upper right)
    link(cx, cy, 601, 313),       // METADATA    (mid right)
    link(cx, cy + 24, 500, 451),  // EVENTS      (lower right)
    link(cx, cy + 24, 142, 440),  // APIS        (lower left)
    link(cx, cy, 87, 214),        // COMPONENTS  (mid left)
  );
  svg.append(tick(210, 150), tick(430, 200), tick(520, 330), tick(210, 400), tick(430, 430));
  // Six nodes evenly distributed around the stack.
  svg.append(
    node(284, 66, 'cube', 'WORKSPACE'),
    node(500, 117, 'grid', 'APPLICATIONS'),
    node(601, 313, 'doc', 'METADATA'),
    node(500, 451, 'bell', 'EVENTS'),
    node(142, 440, 'code', 'APIS'),
    node(87, 214, 'components', 'COMPONENTS'),
  );
  svg.append(centreMark(cx, cy - 26, 104, markUrl));
  return svg;
}

/** Build the requested hero motif with the authentic mark centred. */
export function heroMotif(motif: HeroMotif, markUrl: string): SVGElement {
  return motif === 'stack' ? stack(markUrl) : radar(markUrl);
}
